import React, { useState } from 'react';
import styled from 'styled-components';
import { ConceptLinkerAiLinkerModel } from 'models/ConceptLinkerAiLinkerModel';
import { IConceptLinkerAiLinkerCreate } from 'types/conceptLinkerAiLinker';
import { TextField, Checkbox, FormControlLabel, MenuItem } from '@material-ui/core';

interface IProps {
  model: ConceptLinkerAiLinkerModel;
  courseId: number;
  onClose: () => void;
  onSuccess: (jobId: number) => void;
}

const ENTITY_TYPES = [
  { value: 'sentence', label: 'Sentences' },
  { value: 'segment', label: 'Segments' },
  { value: 'exercise', label: 'Exercises' },
  { value: 'readingText', label: 'Reading Text' },
  { value: 'readingExercise', label: 'Reading Exercises' },
  { value: 'dialog', label: 'Dialogs' },
  { value: 'trainer', label: 'Trainer' },
];

const ConceptLinkerAiLinkerCreate: React.FC<IProps> = ({ model, courseId, onClose, onSuccess }) => {
  const [entityType, setEntityType] = useState('');
  const [lessonStart, setLessonStart] = useState(1);
  const [lessonEnd, setLessonEnd] = useState<number | null>(null);
  const [allLessons, setAllLessons] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleAllLessonsChange = (checked: boolean) => {
    setAllLessons(checked);
    if (checked) {
      setLessonEnd(null);
    } else {
      setLessonEnd(lessonStart + 10);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!entityType) {
      newErrors.entityType = 'Entity type is required';
    }

    if (lessonStart < 1) {
      newErrors.lessonStart = 'Lesson start must be at least 1';
    }

    if (!allLessons && lessonEnd !== null && lessonEnd < lessonStart) {
      newErrors.lessonEnd = 'Lesson end must be >= lesson start';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const data: IConceptLinkerAiLinkerCreate = {
        courseId,
        entityType,
        lessonStart,
        lessonEnd: allLessons ? null : lessonEnd,
      };

      const response = await model.createJob(data);
      if (response.data?.id) {
        onSuccess(response.data.id);
      } else {
        setErrors({ submit: response.data?.message || 'No entities found' });
      }
    } catch (error) {
      const errorMsg = (error as any)?.response?.data?.error || 'Failed to create AI Linker job';
      setErrors({ submit: errorMsg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Create AI Linker Job</Title>
        <CloseButton onClick={onClose}>&times;</CloseButton>
      </Header>

      <Content>
        <Section>
          <SectionTitle>1. Entity Type</SectionTitle>
          <TextField
            select
            value={entityType}
            variant="outlined"
            label="Entity Type"
            size="small"
            onChange={(e) => setEntityType(e.target.value)}
            style={{ width: 250 }}
          >
            {ENTITY_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>
          {errors.entityType && <ErrorText>{errors.entityType}</ErrorText>}
        </Section>

        <Section>
          <SectionTitle>2. Lesson Range</SectionTitle>
          <LessonRow>
            <TextField
              value={lessonStart}
              variant="outlined"
              label="Start"
              type="number"
              size="small"
              onChange={(e) => setLessonStart(Math.max(1, parseInt(e.target.value) || 1))}
              style={{ width: 100 }}
            />
            {!allLessons && (
              <TextField
                value={lessonEnd ?? ''}
                variant="outlined"
                label="End"
                type="number"
                size="small"
                onChange={(e) => setLessonEnd(parseInt(e.target.value) || null)}
                style={{ width: 100 }}
              />
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={allLessons}
                  onChange={(e) => handleAllLessonsChange(e.target.checked)}
                  color="primary"
                />
              }
              label="All"
            />
          </LessonRow>
          {errors.lessonStart && <ErrorText>{errors.lessonStart}</ErrorText>}
          {errors.lessonEnd && <ErrorText>{errors.lessonEnd}</ErrorText>}
        </Section>

        <Note>
          All target dialects will be processed simultaneously. Each entity will be processed by the
          guesser and AI agent pipeline, sharing concepts across dialects.
        </Note>

        {errors.submit && <ErrorText>{errors.submit}</ErrorText>}
      </Content>

      <Footer>
        <CancelButton onClick={onClose}>Cancel</CancelButton>
        <CreateButton onClick={handleCreate} disabled={saving}>
          {saving ? 'Creating...' : 'Create Job'}
        </CreateButton>
      </Footer>
    </Container>
  );
};

export default ConceptLinkerAiLinkerCreate;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: white;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 30px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  margin-bottom: 10px;
  font-size: 18px;
  color: #333;
`;

const LessonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Note = styled.div`
  padding: 15px;
  background-color: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 4px;
  color: #1565c0;
  font-size: 14px;
`;

const ErrorText = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 5px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const CreateButton = styled.button`
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
